import React, { useState, useContext } from "react";
import type {
  IDeliveryTerm,
  ITenantNameAndId,
} from "../../../../../../types/types";
import { useFieldArray } from "react-hook-form";
import {
  useStoreState,
  promiseAllSettledLogAndThrow,
  useFormWrapper,
} from "../../../../../../util/util";
import { Form, FormItems } from "../../../../../../layout/FormLayout";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import {
  SaveButtonPrimaryMedium,
  InputRow,
  ButtonContainer,
  FieldContainer,
} from "../../shared";
import {
  DeleteButton,
  InvisibleButton,
  ButtonWithConfirmDialog,
} from "../../../../../../components/Buttons/Buttons";
import { PlusIcon } from "../../../../../../components/Icons/Icons";
import styled, { useTheme } from "styled-components/macro";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import type { AxiosResponse } from "axios";
import Axios from "axios";
import { endpoints } from "../../../../../../endpoints";
import * as Yup from "yup";
import { strings } from "../../../../../../util/strings";
import { yupResolver } from "@hookform/resolvers/yup";
import { ConfirmDialog } from "../../../../../../components/ConfirmDialog/ConfirmDialog";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";

const DeliveryTermFormRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
`;

const DeliveryTermNameContainer = styled.div`
  max-width: 150px;
`;

const DeliveryTermDescriptionContainer = styled.div`
  margin-left: 10px;
  flex-grow: 1;
`;

type FormValues = {
  deliveryTermFields: IDeliveryTerm[];
};

type ConfirmDeleteState = {
  message: string;
  formTerms: IDeliveryTerm[];
  termsToDelete: IDeliveryTerm[];
};

// TODO: to get the "This is a required field" message to appear on the UI,
// I think we need to not use `Yup.array()` and instead dynamically create a
// schema that includes each of the rows of fields in it.
const editDeliveryTermsFormSchema = (t: TFunction) =>
  Yup.object()
    .shape({
      deliveryTermFields: Yup.array(
        Yup.object()
          .shape({
            name: Yup.string().required(strings(t).thisIsARequiredField),
            description: Yup.string().notRequired(),
            id: Yup.string().notRequired(),
          })
          .defined()
      ).defined(),
    })
    .defined();

export const EditDeliveryTerms = ({
  deliveryTerms,
  mutateDeliveryTerms,
  closeEditDeliveryTerms,
}: {
  deliveryTerms: IDeliveryTerm[];
  mutateDeliveryTerms: () => void;
  closeEditDeliveryTerms: () => void;
}) => {
  const theme = useTheme();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { storefront_id } = useStoreState();
  const { t } = useTranslation();

  const [submitting, setSubmitting] = useState(false);

  // When confirmDeleteState is not null, a dialog is shown.
  const [confirmDeleteState, setConfirmDeleteState] =
    useState<ConfirmDeleteState | null>(null);

  const { register, handleSubmit, formState, errors, control } = useFormWrapper(
    {
      defaultValues: {
        deliveryTermFields:
          deliveryTerms.length === 0
            ? [{ name: "", description: "", id: "" }]
            : deliveryTerms,
      },
      resolver: yupResolver(editDeliveryTermsFormSchema(t)),
    }
  );

  const { fields, append, remove } = useFieldArray<IDeliveryTerm, "key">({
    control,
    name: "deliveryTermFields",
    keyName: "key",
  });

  const onSubmit = async (formValues: FormValues) => {
    setSubmitting(true);
    const formTerms = formValues.deliveryTermFields;
    try {
      const termsToDelete = deliveryTerms.filter((term) => {
        const formHasTerm = formTerms.find((ft) => ft.id === term.id);
        return !formHasTerm;
      });

      const defaultTermCheckPromises = termsToDelete.map((term) => {
        return Axios.get<ITenantNameAndId[]>(
          endpoints.v1_storefronts_id_tenant_defaults_deliveryTerms_id(
            storefront_id,
            term.id
          )
        );
      });
      const termCheckResults = await promiseAllSettledLogAndThrow(
        defaultTermCheckPromises
      );

      const buyerNames = termCheckResults.reduce((names: string[], result) => {
        if ("value" in result) {
          // For TypeScript, should always be the case.
          const moreNames = result.value.data.map((record) => record.name);
          return [...names, ...moreNames];
        }
        return names;
      }, []);

      if (buyerNames.length > 0) {
        const buyerNamesString = buyerNames.join(", ");

        const confirmationMessage =
          `You are about to delete shipping terms that are set as the default ` +
          `shipping term for these customers: ${buyerNamesString}. If you ` +
          `proceed then their default shipping term will be cleared, ` +
          `and you should re-set it.`;

        setConfirmDeleteState({
          message: confirmationMessage,
          formTerms,
          termsToDelete,
        });
      } else {
        continueSubmitting(formTerms, termsToDelete);
      }
    } catch (error) {
      notifyError("An error occurred, please try again", { error });
      setSubmitting(false);
    }
  };

  const continueSubmitting = async (
    formTerms: IDeliveryTerm[],
    termsToDelete: IDeliveryTerm[]
  ) => {
    try {
      const deletePromises = termsToDelete.map((term) => {
        // Term was deleted from the form -> delete.
        return Axios.delete(
          endpoints.v1_storefronts_id_deliveryTerms_id(storefront_id, term.id)
        );
      });

      const patchPromises = formTerms.reduce(
        (promises: Promise<AxiosResponse<any>>[], formTerm) => {
          if (formTerm.id) {
            const oldTerm = deliveryTerms.find((tm) => tm.id === formTerm.id);

            const newName = oldTerm && oldTerm.name !== formTerm.name;
            const newDescription =
              oldTerm && oldTerm.description !== formTerm.description;

            if (newName || newDescription) {
              // Term already exists and name or description changed -> patch.

              // The BE will throw an error if "name" is included but it didn't
              // change.
              const payload = {
                ...(newName && { name: formTerm.name }),
                ...(newDescription && { description: formTerm.description }),
              };

              return [
                ...promises,
                Axios.patch(
                  endpoints.v1_storefronts_id_deliveryTerms_id(
                    storefront_id,
                    formTerm.id
                  ),
                  payload
                ),
              ];
            }
          }
          return promises;
        },
        []
      );

      // DELETE requests have to happen before POST requests, to handle the case
      // where the term was deleted and then re-introduced (new term with same
      // name) in the same editing pass. This is because on the backend if the
      // user creates a new term with the same name as a deleted term,
      // the deleted term is re-used (un-deleted, and description updated) that
      // way there is consistency in the IDs of the terms if they are deleted
      // and re-created.
      await promiseAllSettledLogAndThrow(deletePromises);

      const postPromises = formTerms.reduce(
        (promises: Promise<AxiosResponse<any>>[], formTerm) => {
          if (!formTerm.id) {
            // New term -> post.
            return [
              ...promises,
              Axios.post(
                endpoints.v1_storefronts_id_deliveryTerms(storefront_id),
                {
                  name: formTerm.name,
                  description: formTerm.description,
                }
              ),
            ];
          }
          return promises;
        },
        []
      );
      await promiseAllSettledLogAndThrow(patchPromises);
      await promiseAllSettledLogAndThrow(postPromises);

      notifySuccess("Changes saved");
      mutateDeliveryTerms();
      closeEditDeliveryTerms();
      setSubmitting(false);
    } catch (error) {
      notifyError("There was an error saving the changes", { error });
      setSubmitting(false);
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormItems>
          {fields.map((field, index) => {
            return (
              <InputRow key={field.key || "formRow" + index}>
                <FieldContainer>
                  <DeliveryTermFormRow>
                    <DeliveryTermNameContainer>
                      <TextField
                        label={`Name`}
                        // The name property needs to be in this format.
                        name={`deliveryTermFields[${index}].name`}
                        defaultValue={field.name}
                        theref={register({ required: true })}
                        errors={errors}
                        formState={formState}
                        type={"text"}
                      />
                    </DeliveryTermNameContainer>
                    <DeliveryTermDescriptionContainer>
                      <TextField
                        label={`Description`}
                        // The name property needs to be in this format.
                        name={`deliveryTermFields[${index}].description`}
                        defaultValue={field.description}
                        theref={register({ required: true })}
                        errors={errors}
                        formState={formState}
                        type={"text"}
                      />
                    </DeliveryTermDescriptionContainer>
                    <input
                      name={`deliveryTermFields[${index}].id`}
                      defaultValue={field.id}
                      ref={register({ required: false })}
                      type={"hidden"}
                    />
                  </DeliveryTermFormRow>
                </FieldContainer>
                <ButtonContainer>
                  <ButtonWithConfirmDialog
                    Button={DeleteButton}
                    confirmMessage={
                      "Are you sure you want to remove this item?"
                    }
                    handleConfirm={() => remove(index)}
                    // Adding `type="button"` prevents the default (form
                    // submission) so we don't need to use event.preventDefault().
                    type="button"
                    disabled={fields.length === 1}
                    testid={`delete-delivery-term-field-${index}`}
                  />
                </ButtonContainer>
                {index === fields.length - 1 && (
                  <ButtonContainer>
                    <InvisibleButton
                      // Adding `type="button"` prevents the default (form
                      // submission) so we don't need to use event.preventDefault().
                      type="button"
                      onClick={() => {
                        append({ name: "", description: "", id: "" });
                      }}
                    >
                      <PlusIcon
                        width={16}
                        height={16}
                        fill={theme.primaryIconColor}
                      />
                    </InvisibleButton>
                  </ButtonContainer>
                )}
              </InputRow>
            );
          })}
        </FormItems>
        <SaveButtonPrimaryMedium loading={submitting}>
          Save your changes
        </SaveButtonPrimaryMedium>
      </Form>
      {confirmDeleteState && (
        <ConfirmDialog
          show={!!confirmDeleteState}
          closeDialog={() => {
            setSubmitting(false);
            setConfirmDeleteState(null);
          }}
          confirmMessage={confirmDeleteState?.message || ""}
          handleConfirm={() => {
            continueSubmitting(
              confirmDeleteState.formTerms,
              confirmDeleteState.termsToDelete
            );
            setConfirmDeleteState(null);
          }}
        />
      )}
    </>
  );
};
